import type { DocStatus, DocType, DocumentDTO } from '@eriko/shared-types';
import { Badge } from '@eriko/ui/src/components/ui/badge';
import { Button } from '@eriko/ui/src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@eriko/ui/src/components/ui/dropdown-menu';
import { AlertTriangle, MoreVertical, RefreshCw, Trash2 } from 'lucide-react';
import { useAdminDocMutations, useOrgDocuments } from '../../../hooks/useOrgDocuments';

const STATUS_VARIANT: Record<
  DocStatus,
  'success' | 'info' | 'outline' | 'warning' | 'danger' | 'secondary'
> = {
  UPLOADED: 'secondary',
  QUEUED: 'outline',
  READING: 'info',
  PROCESSED: 'success',
  NEEDS_INPUT: 'warning',
  STORED: 'success',
  FAILED: 'danger',
};

const DOC_TYPE_LABEL: Record<DocType, string> = {
  BANK_STATEMENT: 'Bank statement',
  RECEIPT: 'Receipt',
  INVOICE: 'Invoice',
  BILL: 'Bill',
  TAX_DOC: 'Tax doc',
  OTHER: 'Other',
};

export function DocumentsTab({ orgId }: { orgId: string }) {
  const { data, isLoading } = useOrgDocuments(orgId);
  const mutations = useAdminDocMutations(orgId);

  const docs = data ?? [];
  const awaiting = docs.filter((d) => d.status === 'UPLOADED').length;
  const stuck = docs.filter((d) => d.status === 'FAILED').length;

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex gap-4 mb-4">
        <Counter label="Awaiting" value={awaiting} />
        <Counter label="Stuck (failed)" value={stuck} />
        <Counter label="Total" value={docs.length} />
      </div>

      {isLoading && <p className="text-xs text-surface-500">Loading…</p>}

      {!isLoading && docs.length === 0 && (
        <div className="rounded-lg border border-dashed border-surface-300 px-6 py-10 text-center text-sm text-surface-500">
          No documents for this client yet.
        </div>
      )}

      {!isLoading && docs.length > 0 && (
        <ul className="divide-y divide-surface-200 border border-surface-200 rounded-lg bg-surface-0">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-surface-900 truncate">{d.fileName}</div>
                <div className="text-[10px] text-surface-500">
                  {DOC_TYPE_LABEL[d.docType]} · {new Date(d.createdAt).toLocaleDateString()}
                </div>
              </div>
              <Badge variant={STATUS_VARIANT[d.status]}>{d.status.replace(/_/g, ' ')}</Badge>
              {d.status === 'FAILED' && d.errorMessage && (
                <div title={d.errorMessage}>
                  <AlertTriangle className="w-3.5 h-3.5 text-neg" />
                </div>
              )}
              <RowMenu doc={d} mutations={mutations} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Counter({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-surface-0 border border-surface-300 rounded-lg px-4 py-3 min-w-[140px]">
      <div className="text-[9px] font-semibold tracking-[1.2px] uppercase text-surface-400">
        {label}
      </div>
      <div className="font-mono text-lg font-medium mt-0.5 text-surface-900">{value}</div>
    </div>
  );
}

function RowMenu({
  doc,
  mutations,
}: {
  doc: DocumentDTO;
  mutations: ReturnType<typeof useAdminDocMutations>;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="text-surface-400">
          <MoreVertical className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {doc.status === 'FAILED' && (
          <DropdownMenuItem onClick={() => mutations.retry.mutate(doc.id)}>
            <RefreshCw className="w-3 h-3 mr-2" /> Retry
          </DropdownMenuItem>
        )}
        {(doc.status === 'QUEUED' || doc.status === 'READING') && (
          <DropdownMenuItem onClick={() => mutations.markFailed.mutate(doc.id)}>
            <AlertTriangle className="w-3 h-3 mr-2" /> Mark failed
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => {
            if (confirm(`Delete ${doc.fileName}?`)) mutations.remove.mutate(doc.id);
          }}
        >
          <Trash2 className="w-3 h-3 mr-2" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
